/**
 * Dutching Cashback — calculadora de N resultados (2..5) con una casa por selección.
 *
 * Autocontenida a propósito (patrón B): Tailwind, sin jQuery, sin IDs de DOM y sin tocar
 * `@vilibets/vilibets-uikit`. La función pura vive arriba del componente.
 *
 * Especificación completa: `odd/tasks/dutching-cashback.md` (secciones 4 y 5).
 * El modelo lo resuelve `computeDutchingCashback`; el componente sólo presenta el resultado.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Calculators from './Calculators';

// --- Valor del bono por casa (§4.1) -----------------------------------------------

/**
 * Lossback: se cobra sólo si la selección pierde. Base escalonada por tramo.
 * `MIN(floor(apuesta / tramo) * tramo, límite) * porcentaje * retención`.
 * Con `tramo <= 0` no hay escalones y la base es la apuesta entera.
 */
export const lossbackValue = (stake, bond) => {
  const base = bond.tramo > 0 ? Math.floor(stake / bond.tramo) * bond.tramo : stake;
  return Math.min(base, bond.max) * bond.pct * bond.retention;
};

/** Cashback: se cobra siempre. `MIN(apuesta, límite) * porcentaje * retención` (sin tramo). */
export const cashbackValue = (stake, bond) => Math.min(stake, bond.max) * bond.pct * bond.retention;

/**
 * Cuota efectiva una vez descontada la comisión de la casa (`comision` en fracción, como `pct`).
 *
 * Espejo de la calculadora Dutching existente (`OMCalc_Dutch.js`), que aplica por pierna
 * `(o − 1)·(1 − c) + 1`, algebraicamente igual a `o − c·(o − 1)`. La comisión se aplica ANTES de
 * todo cálculo: son las cuotas efectivas las que dimensionan las apuestas y las que producen los
 * retornos. Con comisión 0 la cuota no se toca, y una cuota incompleta (0) se deja tal cual.
 */
export const cuotaConComision = (odds, comision) => {
  if (!odds || !Number.isFinite(comision)) return odds;
  return odds - comision * (odds - 1);
};

// --- Dimensionado de las apuestas (§4.2) -------------------------------------------

/**
 * Resuelve `s * odds = K + lossback(s)` por punto fijo.
 *
 * La ecuación es circular: `b(s)` depende de `s` y `s` depende de `b(s)`. La sucesión
 * `s <- (K + b(s)) / odds`, arrancando en `s = K / odds`, es monótona no decreciente y está
 * acotada, así que siempre converge (con tramos en <= 29 pasos; sin tramos es geométrica).
 *
 * No se usa fórmula cerrada: existe, pero falla en los bordes exactos del tramo por coma
 * flotante (181.440 casos verificados, 0,017% de fallo).
 *
 * @param {number} K Retorno objetivo: `apuesta1 * cuota1 - lossback1`.
 * @param {number} odds Cuota de la casa.
 * @param {{pct:number,max:number,retention:number,tramo:number}} bond Condiciones del lossback.
 * @returns {number} La apuesta, sin redondear.
 */
export const solveStake = (K, odds, bond) => {
  let stake = K / odds;
  for (let i = 0; i < 300; i += 1) {
    const next = (K + lossbackValue(stake, bond)) / odds;
    if (Math.abs(next - stake) < 1e-12) return next;
    stake = next;
  }
  return stake; // inalcanzable: la sucesión converge siempre
};

const lossbackOf = (house) => (house.type === 'lossback' ? lossbackValue(house.stake, house) : 0);
const cashbackOf = (house) => (house.type === 'cashback' ? cashbackValue(house.stake, house) : 0);

const round2 = (value) => Math.round(value * 100) / 100;

/**
 * Calcula el dimensionado completo.
 *
 * @param {Array<{odds:number,type:'none'|'cashback'|'lossback',pct:number,max:number,retention:number,tramo:number}>} houses
 *   Una casa por selección. `houses[0]` es la referencia y su apuesta la fija el usuario.
 * @param {number} stake1 Apuesta de la casa 1, en euros.
 * @param {{ roundStakes?: boolean }} [options] `roundStakes: false` devuelve la capa exacta,
 *   sin redondear, que es la de los valores de oro de §5.
 * @returns {object} `houses` con la apuesta ya resuelta, totales, pagos por escenario y beneficio.
 */
export function computeDutchingCashback(houses, stake1, { roundStakes = true } = {}) {
  // La comisión (fracción; si la casa no trae el campo o no es un número, vale 0, para que las
  // casas construidas sin él se comporten igual que siempre) se resuelve UNA sola vez: a partir
  // de aquí todas las cuotas del cálculo son las efectivas, y la tecleada se conserva en `odds`
  // para poder mostrarla junto a la efectiva en la tabla de resultados.
  const resolved = houses.map((house) => {
    const comision = Number.isFinite(house.comision) ? house.comision : 0;
    return { ...house, comision, oddsEfectiva: cuotaConComision(house.odds, comision) };
  });
  resolved[0].stake = stake1;

  const lossback1 = lossbackOf(resolved[0]);
  const K = resolved[0].oddsEfectiva * stake1 - lossback1;

  for (let i = 1; i < resolved.length; i += 1) {
    const house = resolved[i];
    house.stake = house.type === 'lossback' ? solveStake(K, house.oddsEfectiva, house) : K / house.oddsEfectiva;
  }

  // §4.4: se resuelve sin redondear y solo entonces se redondea a céntimos. Los pagos se
  // recalculan después con las apuestas ya redondeadas, que son las que se apuestan de verdad.
  if (roundStakes) {
    for (let i = 1; i < resolved.length; i += 1) resolved[i].stake = round2(resolved[i].stake);
  }

  const lossbacks = resolved.map(lossbackOf);
  const cashbacks = resolved.map(cashbackOf);
  const total = resolved.reduce((acc, house) => acc + house.stake, 0);
  const B = lossbacks.reduce((acc, value) => acc + value, 0);
  const C = cashbacks.reduce((acc, value) => acc + value, 0);

  // pago(k) = apuesta_k * cuota_efectiva_k + (B - b_k) + C - S   (§4.3, con la comisión ya dentro)
  const payouts = resolved.map((house, i) => house.oddsEfectiva * house.stake + (B - lossbacks[i]) + C - total);

  const profit = Math.min(...payouts);
  return {
    houses: resolved,
    total,
    lossbacks,
    cashbacks,
    K,
    B,
    C,
    payouts,
    profit,
    roi: profit / total,
    spread: Math.max(...payouts) - profit,
  };
}

/**
 * Descompone una fila de la tabla de resultados.
 *
 * La fila `k` es el ESCENARIO en el que gana la selección `k`, y en ese escenario el lossback
 * propio NO se cobra: sólo pagaría si esa selección perdiera (§4.1). Lo que sí se cobra es el
 * lossback de las otras más TODOS los cashbacks, que son "siempre".
 *
 * De ahí que la fila cierre contra la columna de pago:
 *
 *   bruto + propio + ajeno − total === pago            (§4.3)
 *
 * Incluir el lossback propio en `propio` haría sumar `b_k` de más y contradiría el pago.
 * Por eso `propio` es sólo el cashback: es lo único que esta selección aporta si gana.
 */
export const rowBreakdown = (result, index) => {
  const propio = result.cashbacks[index];
  return {
    // El bruto sale de la cuota EFECTIVA (la que produce el retorno de verdad): si usara la
    // tecleada, la fila dejaría de cuadrar contra la columna de pago en cuanto haya comisión.
    bruto: result.houses[index].oddsEfectiva * result.houses[index].stake,
    propio,
    ajeno: result.B - result.lossbacks[index] + result.C - propio,
    pago: result.payouts[index],
  };
};

// --- Presentación (patrón B: Tailwind, sin jQuery y sin IDs de DOM) -----------------

const EUR = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const PORCENTAJE = new Intl.NumberFormat('es-ES', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const CUOTA = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const MIN_SELECCIONES = 2;
const MAX_SELECCIONES = 5;
const SELECCIONES_POR_DEFECTO = 3;

/** El aviso de desfase sólo se muestra si al formatear a céntimos se ve al menos uno. */
export const desfaseVisible = (spread) => Math.round(spread * 100) > 0;

/** Los tres tipos de bono de §3.1: qué son, cómo se abrevian en el botón y qué dice su `title`. */
const TIPOS_BONO = [
  { value: 'none', corto: 'NO', descripcion: 'Sin bono' },
  { value: 'cashback', corto: 'CB', descripcion: 'Cashback: se cobra siempre' },
  { value: 'lossback', corto: 'LB', descripcion: 'Lossback: se cobra solo si pierde' },
];

/** Claves en la URL de cada casa (decisión 19: se persiste todo, incluido el tipo de bono). */
const claves = (i) => ({
  odds: `dc_cuota${i}`,
  comision: `dc_comision${i}`,
  type: `dc_bono${i}`,
  pct: `dc_pct${i}`,
  max: `dc_max${i}`,
  retention: `dc_retencion${i}`,
  tramo: `dc_tramo${i}`,
});

const parametro = (params, key, fallback = '') => {
  const value = params.get(key);
  return value === null || value === '' ? fallback : value;
};

/**
 * Tipo de bono con el que arranca cada fila cuando la URL no dice nada: la primera sin bono y las
 * demás con lossback, que es la configuración habitual (decisión 29). Es solo un valor por defecto:
 * en cuanto el usuario toca el grupo de radios, su elección queda en la URL y manda sobre esto.
 */
const tipoPorDefecto = (seleccion) => (seleccion === 1 ? 'none' : 'lossback');

/** Acepta coma decimal: aquí se escribe 1,8182. */
const aNumero = (value) => {
  const parsed = Number.parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

// --- Validación de los parámetros del bono (sobre los textos CRUDOS de la URL) ------

/** Campos que exige cada tipo de bono. El tramo sólo existe en lossback (§3.2). */
const CAMPOS_BONO = {
  cashback: ['pct', 'max', 'retention'],
  lossback: ['pct', 'max', 'retention', 'tramo'],
};

/** Cómo se nombra cada campo en el aviso. El 0 del tramo es "sin escalones" (decisión 9). */
const ETIQUETA_CAMPO = {
  pct: 'el porcentaje',
  max: 'el límite',
  retention: 'la retención',
  tramo: 'el tramo (0 si no hay escalones)',
};

/**
 * Casas con bono a las que les falta algún parámetro, con el detalle de cuáles.
 * Recibe los textos CRUDOS de la URL: así un 0 escrito no se confunde con un campo vacío.
 * @param {Array<{type:string,pct:string,max:string,retention:string,tramo:string}>} crudas
 * @returns {Array<{seleccion:number, campos:string[]}>} [] si están todas completas.
 */
export const parametrosBonoFaltantes = (crudas) =>
  crudas
    .map((cruda, i) => ({
      seleccion: i + 1,
      campos: (CAMPOS_BONO[cruda.type] || []).filter((campo) => cruda[campo].trim() === ''),
    }))
    .filter(({ campos }) => campos.length > 0);

/** Enumera en español: "a", "a y b", "a, b y c". */
const enumerar = (elementos) =>
  elementos.length === 1 ? elementos[0] : `${elementos.slice(0, -1).join(', ')} y ${elementos[elementos.length - 1]}`;

/** Arma el aviso: "Completa el porcentaje y el límite del bono de la selección 2". */
export const textoBonoFaltante = (faltantes) => {
  if (faltantes.length === 0) return '';
  const selecciones = faltantes.map(({ seleccion }) => seleccion);
  const campos = [...new Set(faltantes.flatMap(({ campos }) => campos))].map((campo) => ETIQUETA_CAMPO[campo]);
  const donde =
    selecciones.length === 1 ? `de la selección ${selecciones[0]}` : `de las selecciones ${selecciones.join(', ')}`;
  return `Completa ${enumerar(campos)} del bono ${donde}`;
};

/**
 * Deja sólo dígitos y un único separador decimal (que aquí se escribe con coma).
 *
 * Es el equivalente de `type="number"` sin su problema: con el valor controlado desde la URL,
 * `type="number"` devuelve `""` mientras se teclea el estado intermedio "1,", así que el campo se
 * borra y no hay forma de escribir un decimal. Filtrando en el onChange se bloquean las letras
 * igual y los decimales siguen funcionando.
 */
export const soloNumeros = (valor) => {
  const limpio = valor.replace(/[^0-9.,]/g, '');
  const separador = limpio.search(/[.,]/);
  if (separador === -1) return limpio;
  return limpio.slice(0, separador + 1) + limpio.slice(separador + 1).replace(/[.,]/g, '');
};

const celdaClase = 'px-2 py-1 text-center border-black-4 border-t-2';
const encabezadoClase = 'px-2 py-1 text-center font-semibold text-sm text-black';
// Las cabeceras de la tabla de configuración no se parten nunca en dos líneas: «Comisión (%)» caía como
// «Comisión» arriba y «(%)» abajo, porque la columna se mide por el input de 80 px que contiene. Son
// rótulos cortos, así que el `nowrap` no ensancha nada de más; en la tabla de resultados NO se aplica,
// porque allí hay rótulos largos («Pago si gana esta selección») que sí conviene poder repartir.
const encabezadoConfigClase = `${encabezadoClase} whitespace-nowrap`;
// La etiqueta del pie se lee como la cabecera de una columna (mismo tipo de letra, tamaño y color)
// pero SIN la negrita: no es un encabezado, es una etiqueta.
const etiquetaPieClase = `${celdaClase} text-sm text-black`;
// Franja «cebra» del cuerpo: el sombreado cae en las filas PARES, así la fila 1 sale del mismo
// color que el encabezado y no quedan dos filas seguidas del mismo tono. Va en las DOS tablas
// (configuración y resultados): en la Dutching 2FBs campos y resultados conviven en una sola, y
// aquí están repartidos, así que con la franja en una sola la calculadora se leería a dos
// velocidades y no habría forma de seguir una selección de una tabla a la otra.
//
// Los dos tonos viven aquí y sólo aquí, y tienen que ir juntos: en el tema claro la tarjeta es
// `--color-white` (#FFFFFF) y la sombra es el gris `--color-black-2` (#F2F2F4); en el oscuro la
// tarjeta pasa a #161b22 y ese gris claro ya no se distingue del fondo, así que la franja lleva su
// par oscuro `--color-black-11` (#313338). Con un solo tono la alternancia desaparecería al
// cambiar de tema.
const franjaFilaClase = 'bg-black-2 dark:bg-black-11';
// Base sin ancho: el ancho lo pone cada uso (dos utilidades de ancho en el mismo elemento dependen
// del orden de emisión de Tailwind, que ya dio problemas en este fichero).
// Los campos llevan altura fija: un `select` y un `input` con el mismo padding y borde NO quedan
// igual de altos, porque cada uno trae su propia altura intrínseca. Fijando la altura son iguales
// por construcción, y de paso coinciden todos los campos de la calculadora.
const campoBase = 'h-9 rounded-xl border-black-4 border-2 p-1 bg-white dark:text-accent-dark dark:bg-white';
const campoClase = `w-full ${campoBase}`;
const inputClase = `${campoClase} text-center`;
// La cantidad apostada se escribe alineada a la izquierda, con el mismo criterio que los
// desplegables: al importe se lo lee mejor pegado al borde del campo que centrado.
const inputImporteClase = `${campoClase} text-left`;
// La cuota y la comisión llevan ancho fijo, el mismo que la calculadora Dutching (80 px dentro de
// una caja de 90 px), para que el recuadro no se encoja ni se estire cuando cambia el contenido o
// el reparto de columnas. Va en píxeles y no en `w-20` porque el ancho de los inputs de la Dutching
// es un valor literal. El ancho vive SÓLO en esta constante: los dos campos la comparten.
const inputCuotaComisionClase = `w-[80px] ${campoBase} text-center`;
// Los desplegables van alineados a la izquierda: centrados se corta el texto de la opción.
const selectClase = `${campoClase} text-left`;
const etiquetaBase = 'font-semibold text-sm text-black';
// En el panel superior la etiqueta va a la izquierda, pegada al borde del campo: así queda alineada
// con el contenido del input, que también va a la izquierda. El resto de etiquetas del fichero no
// usa la variante centrada, así que sólo existe esta.
const etiquetaPanelClase = `${etiquetaBase} text-left`;

/**
 * Ayuda del "Tipo de bono": un botón `?` junto al encabezado que abre un pequeño modal explicativo.
 *
 * Es estado local transitorio de interfaz y NO viaja en la URL (decisión 19: sólo se persisten las
 * entradas de la calculadora). Va como capa fija a pantalla completa y no como tooltip flotante
 * junto a la celda: la tabla vive dentro de un `overflow-x-auto` que recorta cualquier popup
 * posicionado de forma absoluta, así que la capa fija es la única colocación que no puede quedar
 * cortada.
 */
const AyudaTipoBono = () => {
  const [ayudaAbierta, setAyudaAbierta] = useState(false);

  // Mientras el modal está abierto, Escape lo cierra (es lo que espera el teclado en un diálogo).
  useEffect(() => {
    if (!ayudaAbierta) return undefined;
    const alPulsar = (event) => {
      if (event.key === 'Escape') setAyudaAbierta(false);
    };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [ayudaAbierta]);

  return (
    <>
      {/* El `?` es un botón real y no un icono decorativo: lo abre y lo cierra (toggle). */}
      <button
        type="button"
        aria-expanded={ayudaAbierta}
        aria-label="Qué significan NO, CB y LB"
        title="Qué significan NO, CB y LB"
        onClick={() => setAyudaAbierta(!ayudaAbierta)}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-black text-white text-[10px] leading-none">
        ?
      </button>
      {ayudaAbierta && (
        /* El fondo captura el clic para cerrar; el panel corta la propagación para que un clic
           dentro del texto no cierre el modal. El tinte usa `bg-gray-900/50` y no `bg-black/40`:
           `black` aquí es una variable CSS y la opacidad con barra no compilaría sobre ella. */
        /* El modal se cuelga de la cabecera «Tipo de bono», que lleva `whitespace-nowrap`, y `white-space` se
           HEREDA: sin este `whitespace-normal` el texto del popup no corta línea y se desborda del panel. El
           modal declara lo que necesita en vez de depender de dónde cuelga. */
        /* Lo mismo pasa con el peso del texto: la cabecera lleva `font-semibold` y `font-weight` también se
           HEREDA, así que sin este `font-normal` el cuerpo del modal saldría en semibold y los <strong> de la
           lista dejarían de destacar. El título y el botón Cerrar declaran su propia negrita y no la pierden. */
        <div
          className="font-normal whitespace-normal fixed inset-0 z-30 flex items-start justify-center p-4 pt-24 bg-gray-900/50"
          onClick={() => setAyudaAbierta(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Tipos de bono"
            className="w-full max-w-md rounded-2xl border-2 border-black-4 bg-white p-4 text-left shadow-lg"
            onClick={(event) => event.stopPropagation()}>
            <p className="font-semibold text-sm text-black">Tipos de bono</p>
            <ul className="mt-2 list-disc pl-4 text-sm text-black dark:text-accent-dark space-y-1">
              <li>
                <strong>NO — Sin bono</strong>: esta selección no lleva bono; se apuesta solo con la cuota.
              </li>
              <li>
                <strong>CB — Cashback (se cobra siempre)</strong>: porcentaje sobre{' '}
                <code>MIN(apuesta, límite) × retención</code>. Se cobra gane o pierda.
              </li>
              <li>
                <strong>LB — Lossback (se cobra solo si pierde)</strong>: porcentaje sobre{' '}
                <code>MIN(tramo acumulado, límite) × retención</code>. Solo se cobra si la selección pierde. Tramo 0 =
                sin escalones.
              </li>
            </ul>
            <p className="mt-2 text-sm text-black dark:text-accent-dark">
              Los campos de porcentaje, límite y retención (y el tramo en LB) se piden al elegir un tipo con bono.
            </p>
            <div className="mt-3 text-center">
              <button
                type="button"
                autoFocus
                onClick={() => setAyudaAbierta(false)}
                className="rounded-xl border-black-4 border-2 bg-white px-3 py-1 text-sm font-semibold text-black dark:text-accent-dark">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const DutchingCashbackCard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // La URL es el estado. `replace` porque con 5 casas y 6 campos por casa un push por tecla
  // llenaría el historial de entradas inútiles.
  const setParametro = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value === null || value === undefined) next.delete(key);
    else next.set(key, String(value));
    setSearchParams(next, { replace: true });
  };

  const selecciones = Math.min(
    MAX_SELECCIONES,
    Math.max(
      MIN_SELECCIONES,
      Math.round(aNumero(parametro(searchParams, 'dc_selecciones', SELECCIONES_POR_DEFECTO))) || SELECCIONES_POR_DEFECTO
    )
  );
  const apuesta1 = aNumero(parametro(searchParams, 'dc_apuesta'));

  /** Los campos numéricos aceptan sólo dígitos y un separador decimal, como el resto de las
   * calculadoras; las letras se filtran antes de guardar en la URL. */
  const setNumero = (key, valor) => setParametro(key, soloNumeros(valor));

  // La validación trabaja con los textos crudos de la URL (un 0 escrito es válido y un vacío no);
  // el cálculo, con números. Las dos capas se alimentan de la misma lista para no divergir.
  const crudas = [];
  for (let i = 1; i <= selecciones; i += 1) {
    const k = claves(i);
    crudas.push({
      odds: parametro(searchParams, k.odds),
      // La comisión es opcional y su valor neutro es 0: por eso el campo muestra SIEMPRE un valor
      // (el 0 por defecto) y NO entra en la validación de vacíos, que sí bloquea a los del bono.
      comision: parametro(searchParams, k.comision, '0'),
      type: parametro(searchParams, k.type, tipoPorDefecto(i)),
      pct: parametro(searchParams, k.pct),
      max: parametro(searchParams, k.max),
      retention: parametro(searchParams, k.retention),
      tramo: parametro(searchParams, k.tramo),
    });
  }
  const casas = crudas.map((cruda) => ({
    odds: aNumero(cruda.odds),
    comision: aNumero(cruda.comision) / 100,
    type: cruda.type,
    pct: aNumero(cruda.pct) / 100,
    max: aNumero(cruda.max),
    retention: aNumero(cruda.retention) / 100,
    tramo: aNumero(cruda.tramo),
  }));

  const sinCuota = casas.map((casa, i) => (casa.odds > 0 ? null : i + 1)).filter((i) => i !== null);
  const faltantesBono = parametrosBonoFaltantes(crudas);
  const listo = sinCuota.length === 0 && apuesta1 > 0 && faltantesBono.length === 0;
  const resultado = listo ? computeDutchingCashback(casas, apuesta1) : null;

  // Prioridad del aviso: primero las cuotas, luego la apuesta y por último los parámetros del bono.
  const faltaTexto =
    sinCuota.length > 0
      ? sinCuota.length === 1
        ? `Completa la cuota de la selección ${sinCuota[0]}`
        : `Completa la cuota de las selecciones ${sinCuota.join(', ')}`
      : apuesta1 <= 0
      ? 'Completa la cantidad apostada de la selección 1'
      : textoBonoFaltante(faltantesBono);

  const filaCasa = (casa, index) => {
    const k = claves(index + 1);
    const tieneBono = casa.type !== 'none';

    // La franja cebra: `index` es 0-based, así que las filas pares de verdad son los índices impares.
    return (
      <tr key={index} className={index % 2 === 1 ? franjaFilaClase : undefined}>
        {/* El número de la columna Selección va sin negrita, igual que en la tabla de resultados:
            sólo los encabezados llevan `font-semibold` (se mantienen las dos tablas consistentes). */}
        <td className={celdaClase}>{index + 1}</td>
        <td className={celdaClase}>
          <input
            className={inputCuotaComisionClase}
            type="text"
            inputMode="decimal"
            placeholder="1,85"
            value={parametro(searchParams, k.odds)}
            onChange={(event) => setNumero(k.odds, event.target.value)}
          />
        </td>
        <td className={celdaClase}>
          <input
            className={inputCuotaComisionClase}
            type="text"
            inputMode="decimal"
            value={parametro(searchParams, k.comision, '0')}
            onChange={(event) => setNumero(k.comision, event.target.value)}
          />
        </td>
        <td className={celdaClase}>
          {/* Grupo de radios con aspecto de botón: un solo clic por tipo y los tres a la vista. El input va
              DENTRO del label (así no hacen falta ids de DOM, que es la premisa del patrón B) y `peer` alcanza
              al texto porque son hermanos. El estado seleccionado lo pinta React y no `peer-checked`, para no
              depender del orden en que Tailwind emite las variantes frente a `dark:`. */}
          <div
            className="flex items-center justify-center gap-1"
            role="group"
            aria-label={`Tipo de bono de la selección ${index + 1}`}>
            {TIPOS_BONO.map((tipo) => {
              const activo = casa.type === tipo.value;

              return (
                <label key={tipo.value} className="cursor-pointer" title={tipo.descripcion}>
                  {/* El nombre accesible es la descripción completa porque lo visible es sólo la
                      abreviatura NO/CB/LB: un lector de pantallas necesita leer el texto entero. */}
                  <input
                    className="peer sr-only"
                    type="radio"
                    name={`dc_tipo_bono_${index + 1}`}
                    value={tipo.value}
                    checked={activo}
                    aria-label={tipo.descripcion}
                    onChange={(event) => setParametro(k.type, event.target.value)}
                  />
                  {/* El "sin bono" seleccionado va en rojo y no en azul: así se distingue a simple
                      vista la fila que no lleva bono de las dos que sí lo llevan. */}
                  <span
                    className={`block min-w-[2.25rem] rounded-lg border-black-4 border-2 px-1 py-1 text-center text-xs font-semibold peer-focus-visible:ring-2 peer-focus-visible:ring-secondary ${
                      activo
                        ? tipo.value === 'none'
                          ? 'bg-warning-red text-white'
                          : 'bg-primary text-white'
                        : 'bg-white text-black dark:text-accent-dark'
                    }`}>
                    {tipo.corto}
                  </span>
                </label>
              );
            })}
          </div>
        </td>
        <td className={celdaClase}>
          {tieneBono && (
            <input
              className={inputClase}
              type="text"
              inputMode="decimal"
              value={parametro(searchParams, k.pct)}
              onChange={(event) => setNumero(k.pct, event.target.value)}
            />
          )}
        </td>
        <td className={celdaClase}>
          {tieneBono && (
            <input
              className={inputClase}
              type="text"
              inputMode="decimal"
              value={parametro(searchParams, k.max)}
              onChange={(event) => setNumero(k.max, event.target.value)}
            />
          )}
        </td>
        <td className={celdaClase}>
          {tieneBono && (
            <input
              className={inputClase}
              type="text"
              inputMode="decimal"
              value={parametro(searchParams, k.retention)}
              onChange={(event) => setNumero(k.retention, event.target.value)}
            />
          )}
        </td>
        <td className={celdaClase}>
          {/* El tramo sólo aplica al lossback: en cashback no hay pérdida cuando se gana (§3.2). */}
          {casa.type === 'lossback' && (
            <input
              className={inputClase}
              type="text"
              inputMode="decimal"
              value={parametro(searchParams, k.tramo)}
              onChange={(event) => setNumero(k.tramo, event.target.value)}
            />
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="flex flex-col shadow rounded-2xl bg-white mx-auto w-full" style={{ maxWidth: '1100px' }}>
      <div className="font-medium px-4 py-3 bg-black text-white text-center text-base capitalize rounded-2xl rounded-b-none">
        Dutching Cashback
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-4 px-5 py-4 my-3 mx-3 bg-primary-2 rounded-2xl">
        {/* Ancho fluido con tope: en móvil los campos llenan la fila y en escritorio no crecen más
            allá de su tamaño de siempre (el máximo lo fija la clase, no un ancho en línea). */}
        <label className="flex w-full max-w-[16rem] flex-col gap-1 dark:text-accent-dark">
          <span className={etiquetaPanelClase}>Cantidad apostada (selección 1)</span>
          <input
            className={inputImporteClase}
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={parametro(searchParams, 'dc_apuesta')}
            onChange={(event) => setNumero('dc_apuesta', event.target.value)}
          />
        </label>

        <label className="flex w-full max-w-[16rem] flex-col gap-1 dark:text-accent-dark">
          <span className={etiquetaPanelClase}>Número de selecciones</span>
          <select
            className={selectClase}
            value={selecciones}
            onChange={(event) => setParametro('dc_selecciones', event.target.value)}>
            {Array.from({ length: MAX_SELECCIONES - MIN_SELECCIONES + 1 }, (_, i) => MIN_SELECCIONES + i).map(
              (valor) => (
                <option key={valor} value={valor}>
                  {valor}
                </option>
              )
            )}
          </select>
        </label>
      </div>

      <div className="overflow-x-auto px-3">
        {/* Una columna más que la tabla de resultados (860 px): la de la comisión. */}
        <table className="w-full" style={{ minWidth: '950px' }}>
          <thead>
            <tr>
              <th className={encabezadoConfigClase}>Selección</th>
              <th className={encabezadoConfigClase}>Cuota</th>
              <th className={encabezadoConfigClase}>Comisión (%)</th>
              <th className={encabezadoConfigClase}>
                Tipo de bono
                <AyudaTipoBono />
              </th>
              <th className={encabezadoClase}>Porcentaje de lo apostado convertible en bono (%)</th>
              <th className={encabezadoClase}>Límite máximo bonificable (€)</th>
              <th className={encabezadoClase}>Retención del bono/FB (%)</th>
              <th className={encabezadoConfigClase}>Tramo (€)</th>
            </tr>
          </thead>
          <tbody>{casas.map(filaCasa)}</tbody>
        </table>
      </div>

      {resultado === null ? (
        <p className="text-center text-black-7 py-10">{faltaTexto} para ver el resultado.</p>
      ) : (
        <>
          <div className="overflow-x-auto px-3 mt-4">
            <table className="w-full" style={{ minWidth: '860px' }}>
              <thead>
                <tr>
                  <th className={encabezadoClase}>Selección</th>
                  <th className={encabezadoClase}>Cuota</th>
                  <th className={encabezadoClase}>Cantidad a Apostar</th>
                  <th className={encabezadoClase}>Retorno bruto</th>
                  <th className={encabezadoClase}>Bono de esta selección</th>
                  <th className={encabezadoClase}>Bono de las otras</th>
                  <th className={encabezadoClase}>Pago si gana esta selección</th>
                </tr>
              </thead>
              <tbody>
                {resultado.houses.map((casa, i) => {
                  const fila = rowBreakdown(resultado, i);

                  // La franja cebra, con el mismo criterio que la tabla de configuración.
                  return (
                    <tr key={i} className={i % 2 === 1 ? franjaFilaClase : undefined}>
                      {/* Sin negrita, igual que la columna Selección de la tabla de configuración. */}
                      <td className={celdaClase}>{i + 1}</td>
                      <td className={celdaClase}>
                        {/* Cuota EFECTIVA; sólo con comisión se añade entre paréntesis la tecleada.
                            Así, sin comisión, la fila sigue cumpliendo cuota × apuesta = bruto. */}
                        {CUOTA.format(casa.oddsEfectiva)}
                        {casa.comision > 0 ? ` (${CUOTA.format(casa.odds)})` : ''}
                      </td>
                      <td className={celdaClase + ' font-semibold'}>{EUR.format(casa.stake)}</td>
                      <td className={celdaClase}>{EUR.format(fila.bruto)}</td>
                      <td className={celdaClase}>{EUR.format(fila.propio)}</td>
                      <td className={celdaClase}>{EUR.format(fila.ajeno)}</td>
                      <td className={celdaClase}>
                        <span
                          className={fila.pago < 0 ? 'text-warning-red font-semibold' : 'text-secondary font-semibold'}>
                          {EUR.format(fila.pago)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  {/* La etiqueta ocupa las dos primeras columnas y el importe cae en la tercera, bajo
                      "Cantidad a Apostar": así el total no queda debajo de "Retorno bruto". Es el mismo
                      criterio que usa la Dutching existente del UI kit. */}
                  <td className={etiquetaPieClase} colSpan={2}>
                    Cantidad Total Apostada:
                  </td>
                  <td className={celdaClase + ' dark:text-accent-dark'}>{EUR.format(resultado.total)}</td>
                  <td className={celdaClase} colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 px-5 py-4 mb-3 mt-2 mx-3 bg-primary-2 rounded-2xl">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-black dark:text-accent-dark">Beneficio garantizado:</span>
              <span
                className={
                  resultado.profit < 0
                    ? 'text-warning-red font-semibold text-xl'
                    : 'text-secondary font-semibold text-xl'
                }>
                {EUR.format(resultado.profit)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-black dark:text-accent-dark">ROI:</span>
              <span
                className={
                  resultado.roi < 0 ? 'text-warning-red font-semibold text-xl' : 'text-secondary font-semibold text-xl'
                }>
                {PORCENTAJE.format(resultado.roi)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-black dark:text-accent-dark">Lossback total:</span>
              <span className="dark:text-accent-dark">{EUR.format(resultado.B)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-black dark:text-accent-dark">Cashback total:</span>
              <span className="dark:text-accent-dark">{EUR.format(resultado.C)}</span>
            </div>
            {desfaseVisible(resultado.spread) && (
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-black dark:text-accent-dark">
                  Desfase por redondeo a céntimos:
                </span>
                <span className="dark:text-accent-dark">{EUR.format(resultado.spread)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Página de la calculadora.
 *
 * La tarjeta va dentro de `Calculators`, que aporta las pestañas superiores, el wizard y el layout
 * que comparten todas las calculadoras. "Autocontenida" (decisión 15) significa que no se toca el
 * UI kit ni hay que republicarlo: el envoltorio común se usa igual que en el resto.
 */
const DutchingCashback = () => (
  <Calculators>
    <DutchingCashbackCard />
  </Calculators>
);

export default DutchingCashback;
